import {createApi, fetchBaseQuery, retry} from '@reduxjs/toolkit/query/react';
import {TRACK_API_BASE_URL} from 'utils/consts';
import {tsToNy} from 'utils/helpers';

const getCsrfToken = () =>
  document.cookie
    .split('; ')
    .find((row) => row.startsWith('csrftoken='))
    ?.split('=')[1];

const staggeredBaseQuery = retry(
  async (args, api, extraOptions) => {
    const fetchBase = fetchBaseQuery({
      baseUrl: TRACK_API_BASE_URL,
      credentials: 'include',
      prepareHeaders: (headers, {type}) => {
        if (type !== 'query') {
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            headers.set('X-CSRFToken', csrfToken);
          }
        }
        return headers;
      },
    });
    return fetchBase(args, api, extraOptions);
  },
  {
    retryCondition: (error, args, extraArgs) => {
      // Don't retry mutations
      if (args.method && args.method !== 'GET') {
        return false;
      }
      const isAuthError =
        error.status === 401 ||
        error.status === 403 ||
        error.originalStatus === 401 ||
        error.originalStatus === 403;
      // /auth/me/ can be queried right after login, before the new session
      // has fully committed server-side, so give it a couple retries on auth
      // errors instead of bailing immediately like other endpoints do.
      if (isAuthError && extraArgs.baseQueryApi.endpoint === 'getMe') {
        return extraArgs.attempt < 3;
      }
      // bail out after 3 attempts or on auth errors
      if (extraArgs.attempt >= 3 || isAuthError) {
        return false;
      } else {
        return true;
      }
    },
    backoff: (attempt) =>
      new Promise((resolve) => {
        setTimeout(resolve, Math.pow(2, attempt) * 1000);
      }),
  }
);

// Dedicated CSRF-safe query for the refresh call — reusing staggeredBaseQuery
// here would inherit the *original* failing request's `type` (query/mutation)
// via the shared `api` object, so its prepareHeaders would decide the CSRF
// header based on the wrong request.
// Built lazily (not at module load) so importing this module never requires
// TRACK_API_BASE_URL unless a reauth attempt actually happens.
let _refreshBaseQuery;
const refreshBaseQuery = (...args) => {
  if (!_refreshBaseQuery) {
    _refreshBaseQuery = fetchBaseQuery({
      baseUrl: TRACK_API_BASE_URL,
      credentials: 'include',
      prepareHeaders: (headers) => {
        const csrfToken = getCsrfToken();
        if (csrfToken) {
          headers.set('X-CSRFToken', csrfToken);
        }
        return headers;
      },
    });
  }
  return _refreshBaseQuery(...args);
};

const REAUTH_EXCLUDED_ENDPOINTS = new Set(['getMe', 'refreshToken']);

let refreshPromise = null;

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await staggeredBaseQuery(args, api, extraOptions);

  const status = result.error?.status ?? result.error?.originalStatus;
  const isLoggedIn = Boolean(api.getState().auth?.user);
  const isExcluded = REAUTH_EXCLUDED_ENDPOINTS.has(api.endpoint);

  if (status !== 401 || !isLoggedIn || isExcluded) {
    return result;
  }

  if (!refreshPromise) {
    refreshPromise = refreshBaseQuery(
      {url: '/auth/refresh/', method: 'POST'},
      api,
      extraOptions
    ).finally(() => {
      refreshPromise = null;
    });
  }
  const refreshResult = await refreshPromise;

  if (refreshResult.error) {
    // Dynamically imported to avoid a circular init dependency: this module
    // is loaded by `store`, and reauthFailure's chain (SessionExpiredPrompt ->
    // loginPopup -> axiosFuncs -> errorHandler) leads back to `store`.
    const {handleReauthFailure} = await import('utils/reauthFailure');
    handleReauthFailure(api.getState);
    return result;
  }

  return staggeredBaseQuery(args, api, extraOptions);
};

const extractRegions = (response) => {
  let regionsWithDuplicates = response
    .map((school) => school.region?.trim())
    .sort();
  const regions = [...new Set(regionsWithDuplicates)];
  return regions;
};

export {baseQueryWithReauth};

export const emptySplitApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'AggregateReportMeta',
    'AuditBySchool',
    'AuditErrorRateByField',
    'AuditErrorRateBySchool',
    'AuditRules',
    'Auth',
    'Categories',
    'CmsPages',
    'ColumnOptions',
    'CourseSelection',
    'DataReportsMeta',
    'DBT',
    'ErrorRateBySchool',
    'ErrorRateSnapshot',
    'Gradespan',
    'HomeroomAssignments',
    'Homerooms',
    'HSScheduling',
    'LatestDownloadsInfo',
    'LightdashToken',
    'LookupUcpid',
    'PersonDetails',
    'Pipeline',
    'PretStats',
    'Report',
    'ReportMeta',
    'ReportsList',
    'ReportSourceColumns',
    'ReportSources',
    'ReportsTree',
    'School',
    'SchoolErrorRateSnapshot',
    'SchoolErrorRateWithStatus',
    'SchoolPretStudents',
    'Schools',
    'SeedData',
    'SeedMetaData',
    'Seeds',
    'SeedVersions',
    'SingleReport',
    'StaffProvisioningStatus',
    'StudentAuditData',
    'StudentData',
    'Students',
    'SupersetToken',
    'TableauToken',
    'Tags',
    'TaskCompletionStats',
    'TaskStatus',
    'TaskStatusBySchool',
    'TeacherAssignments',
  ],
  endpoints: () => ({}),
  keepUnusedDataFor: process.env.NODE_ENV !== 'test' ? 600 : 0,
});

export const apiSlice = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchPipeline: builder.query({
      query: () => `/pipeline/`,
      transformResponse: (response) => {
        const jobStart = response?.[0]?.job_start;

        if (!jobStart) {
          console.warn(
            'fetchPipeline returned an unexpected payload:',
            response
          );
          return null;
        }

        const usaTime = tsToNy(jobStart + 'Z');
        return usaTime;
      },
      providesTags: ['Pipeline'],
    }),
    fetchSchool: builder.query({
      query: (siteId) => `/schools/${siteId}/`,
      transformResponse: (response) => {
        const school = response[0];
        return {
          siteId: school?.site_id,
          existingLowGrade: school?.low_grade,
          existingHighGrade: school?.high_grade,
        };
      },
      providesTags: (result, error, arg) => [{type: 'School', id: arg}],
    }),
    fetchSchools: builder.query({
      query: () => `/schools/`,
      transformResponse: (response) => {
        function cmp(a, b) {
          if (b.abbreviation === null) return -1;
          if (a.abbreviation > b.abbreviation) return 1;
          if (a.abbreviation < b.abbreviation) return -1;
          return 0;
        }

        const regions = extractRegions(response);
        const data = {
          regions,
          schools: response.sort(cmp),
        };
        return data;
      },
      providesTags: ['Schools'],
    }),
    fetchHSScheduling: builder.query({
      query: (siteId) => `/hs_scheduling/${siteId}/`,
      transformResponse: (response) => {
        let options = {};
        for (let i = 0; i < response.length; i++) {
          let course = response[i]; // Add this department if it doesn't exist.
          !(course.department in options) && (options[course.department] = []);
          let courseToAdd = {
            course: course.next_course_number,
            grade: course.grade,
            num_sections:
              course.section_count.draft !== null
                ? course.section_count.draft
                : course.section_count.final,
            students_in_section:
              course.max_students_per_section.draft !== null
                ? course.max_students_per_section.draft
                : course.max_students_per_section.final,
          };
          options[course.department].push(courseToAdd);
        }
        const results = response?.map((e) => {
          let course_name_display =
            e.next_course !== 'non_ap'
              ? e.next_course
              : `Non AP ${e.department} Course`;
          e = {
            course_name_display,
            ...e,
          };
          return e;
        });
        const filterOptions = [
          ...new Set(results.map((e) => e.department)),
        ].map((e) => {
          return {
            text: e,
            value: e,
          };
        });
        return {results, options, filterOptions};
      },
      providesTags: (result, error, arg) => [{type: 'HSScheduling', id: arg}],
    }),
    fetchStaffProvisioningStatus: builder.query({
      query: (siteId) =>
        `/staff_provisioning_status/v2/${
          siteId !== 'all' ? `?system_site_id=${siteId}&site_id=${siteId}` : ''
        }`,
      providesTags: (result, error, arg) => [
        {type: 'StaffProvisioningStatus', id: arg},
      ],
    }),
    lookupUcpid: builder.query({
      query: (ucpid) => `/ucpid/search/?q=${ucpid}`,
      providesTags: (result, error, arg) => [{type: 'LookupUcpid', id: arg}],
    }),
    fetchPersonDetails: builder.query({
      query: (ucpid) => `/ucpid/${ucpid}/`,
      providesTags: (result, error, arg) => [{type: 'PersonDetails', id: arg}],
    }),
    mapPersonUcpid: builder.mutation({
      query: ({primaryUcpid, ucpidToMerge}) => ({
        url: `/ucpid/${ucpidToMerge}/`,
        method: 'POST',
        body: {primary_ucpid: primaryUcpid},
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'LookupUcpid', id: arg.ucpidToMerge},
        {type: 'LookupUcpid', id: arg.primaryUcpid},
        {type: 'PersonDetails', id: arg.ucpidToMerge},
        {type: 'PersonDetails', id: arg.primaryUcpid},
      ],
    }),
    batchMapUcpids: builder.mutation({
      query: (ucpids) => ({
        url: `/ucpid/merge_ucpids/batch/`,
        method: 'POST',
        body: ucpids,
      }),
      invalidatesTags: ['LookupUcpid', 'PersonDetails'],
    }),
    setPersonDetails: builder.mutation({
      query: ({ucpid, details}) => ({
        url: `/ucpid/${ucpid}/`,
        method: 'POST',
        body: details,
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'LookupUcpid', id: arg.ucpid},
        {type: 'PersonDetails', id: arg.ucpid},
      ],
    }),
    setHSScheduleDraft: builder.mutation({
      query: ({siteId, schedule}) => ({
        url: `/hs_scheduling/draft/${siteId}/`,
        method: 'POST',
        body: schedule,
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'HSScheduling', id: arg.siteId},
      ],
    }),
    saveHSSchedule: builder.mutation({
      query: (siteId) => ({
        url: `/hs_scheduling/save/${siteId}/`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'HSScheduling', id: arg},
      ],
    }),
    loadOptimalSchedule: builder.mutation({
      query: (siteId) => ({
        url: `/hs_scheduling/draft/${siteId}/load_optimal/`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, arg) => [
        {type: 'HSScheduling', id: arg},
      ],
    }),
  }),
});

export const {
  useFetchPipelineQuery,
  useFetchSchoolQuery,
  useFetchSchoolsQuery,
  useFetchHSSchedulingQuery,
  useFetchStaffProvisioningStatusQuery,
  useLazyLookupUcpidQuery,
  useFetchPersonDetailsQuery,
  useMapPersonUcpidMutation,
  useBatchMapUcpidsMutation,
  useSetPersonDetailsMutation,
  useSetHSScheduleDraftMutation,
  useSaveHSScheduleMutation,
  useLoadOptimalScheduleMutation,
} = apiSlice;
